import { createFileRoute } from "@tanstack/react-router";
import { trackPhoneClick } from "@/lib/track-phone-click";
import { trackEmailClick } from "@/lib/track-email-click";
import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import heroImg from "@/assets/ib-hero.webp";
import useCaseImg from "@/assets/ib-usecase.webp";
import finishImg from "@/assets/ib-finish.webp";
import anwendungImg from "@/assets/ib-anwendung.webp";
import detailImg from "@/assets/ib-detail.webp";
import ableitImg from "@/assets/ib-ableit.webp";
import teamImg from "@/assets/ib-team.jpg";
import logoImg from "@/assets/logo.png";
import { AttributionFields } from "@/components/AttributionFields";
import { HeroLeadForm } from "@/components/HeroLeadForm";

export const Route = createFileRoute("/industriebodenbeschichtung")({
  head: () => ({
    meta: [
      {
        title:
          "Industriebodenbeschichtung Gewerbe & Industrie | Schneider Bodenbeschichtungen GmbH",
      },
      {
        name: "description",
        content:
          "Industriebodenbeschichtung für Werkstätten, Lagerhallen, Produktion und WHG-/ESD-Bereiche. Familiengeführter Fachbetrieb nach WHG mit über 20 Jahren Erfahrung in NRW. Projekt unverbindlich anfragen.",
      },
      {
        property: "og:title",
        content:
          "Industriebodenbeschichtung für Gewerbe und Industrie – Schneider",
      },
      {
        property: "og:description",
        content:
          "Robuste, fachgerechte Bodenbeschichtungen für gewerblich und industriell genutzte Flächen. WHG-Fachbetrieb, präqualifiziert. Über 20 Jahre Erfahrung.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: heroImg },
      { property: "twitter:image", content: heroImg },
      { name: "robots", content: "index,follow" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://lp.schneider-bodenbeschichtungen.de/industriebodenbeschichtung",
      },
    ],
  }),
  component: LandingV2,
});

const PHONE_TEL = "+492841931010";
const PHONE_DISPLAY = "+49 2841 931010";
const EMAIL = "info@schneider-bbs.de";
const FORM_ENDPOINT = "";

function scrollToForm() {
  document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
}

/* ─── Header ─── */
function Header() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <a href="#hero" className="inline-flex items-center">
          <img
            src={logoImg}
            alt="Schneider Bodenbeschichtungen GmbH"
            className="h-9 w-auto md:h-10"
          />
        </a>
        <a
          href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v1", PHONE_TEL)}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:border-foreground"
        >
          <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current" aria-hidden="true">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
          <span className="sm:hidden">Anrufen</span>
        </a>
      </div>
    </header>
  );
}

/* ─── Sticky Desktop CTA bar ─── */
function StickyDesktopCTA() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    function onScroll() {
      setShow(window.scrollY > 600);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      className={`fixed inset-x-0 top-[57px] z-30 hidden border-b border-border bg-background/95 backdrop-blur-sm transition-transform duration-200 md:block ${
        show ? "translate-y-0" : "-translate-y-full"
      }`}
      aria-hidden={!show}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-2.5">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">
            Industriebodenbeschichtung – Schneider
          </span>
          <span className="hidden text-border lg:inline">•</span>
          <span className="hidden lg:inline">
            Familiengeführter Fachbetrieb · 20+ Jahre Erfahrung · WHG
          </span>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v1", PHONE_TEL)}
            className="hidden h-9 items-center justify-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:border-foreground lg:inline-flex"
          >
            <svg viewBox="0 0 20 20" className="h-3.5 w-3.5 fill-current" aria-hidden="true">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            {PHONE_DISPLAY}
          </a>
          <button
            type="button"
            onClick={scrollToForm}
            className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Projekt anfragen
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Sticky Mobile CTA ─── */
function StickyMobileCTA() {
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 p-2 backdrop-blur-sm md:hidden">
      <div className="flex gap-2">
        <a
          href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v1", PHONE_TEL)}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-foreground px-3 py-2.5 text-sm font-semibold text-foreground"
        >
          Anrufen
        </a>
        <button
          type="button"
          onClick={scrollToForm}
          className="flex flex-1 items-center justify-center rounded-md bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground"
        >
          Projekt anfragen
        </button>
      </div>
    </div>
  );
}

/* ─── Section wrapper ─── */
function Section({
  children,
  className = "",
  id,
  gray = false,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
  gray?: boolean;
}) {
  return (
    <section
      id={id}
      className={`scroll-mt-24 ${gray ? "bg-muted/40" : "bg-background"} border-b border-border ${className}`}
    >
      <div className="mx-auto max-w-6xl px-4 py-16 md:py-24">{children}</div>
    </section>
  );
}

/* ─── Star icon ─── */
function Star() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current" aria-hidden="true">
      <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.79L10 14.77l-5.2 2.74.99-5.79L1.58 7.62l5.82-.85L10 1.5z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 10.5l3.5 3.5 7-8" />
    </svg>
  );
}

/* ─── Hero ─── */
function Hero() {
  return (
    <section
      id="hero"
      className="scroll-mt-24 relative overflow-hidden border-b border-border bg-foreground"
    >
      {/* Full-width background image */}
      <img
        src={heroImg}
        alt="Großflächige Gewerbehalle mit hochwertiger Industriebodenbeschichtung"
        className="absolute inset-0 h-full w-full object-cover opacity-50"
        fetchPriority="high"
      />
      {/* Subtle left-side gradient for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-r from-foreground/80 via-foreground/50 to-transparent" />

      <div className="relative mx-auto max-w-6xl px-4 py-12 md:py-20 lg:py-24">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="min-w-0 lg:col-span-7">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-background/30 bg-background/10 px-3 py-1 text-xs font-medium tracking-wide text-background uppercase backdrop-blur">
            Für Gewerbe & Industrie
          </div>
          <h1 className="text-[1.875rem] font-semibold leading-[1.1] tracking-tight text-background sm:text-4xl md:text-[2.5rem] lg:text-5xl" lang="de">
            Industrieboden&shy;beschichtung für Werkstatt, Halle und Produktion
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-background/90 md:text-lg">
            Rissige, staubende oder stark abgenutzte Böden bremsen den Betrieb.
            Wir planen und realisieren belastbare Beschichtungen für gewerblich
            und industriell genutzte Flächen – fachgerecht, wirtschaftlich und
            passend auf Ihren Einsatzbereich abgestimmt.
          </p>

          <ul className="mt-6 space-y-2.5">
            <li className="flex items-start gap-2.5 text-sm text-background md:text-base">
              <CheckIcon />
              <span>Für Werkstätten, Lagerhallen und Produktionsflächen</span>
            </li>
            <li className="flex items-start gap-2.5 text-sm text-background md:text-base">
              <CheckIcon />
              <span>Auch für Beschichtung im Bestand, WHG-Bereiche und spezielle Anforderungen</span>
            </li>
            <li className="flex items-start gap-2.5 text-sm text-background md:text-base">
              <CheckIcon />
              <span>Ausschließlich für gewerbliche und industrielle Projekte</span>
            </li>
          </ul>

          <div className="mt-8">
            <a
              href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v1", PHONE_TEL)}
              className="inline-flex h-12 items-center justify-center rounded-md border border-background/40 bg-background/10 px-6 text-sm font-semibold text-background backdrop-blur transition-colors hover:bg-background/20"
            >
              Jetzt anrufen · {PHONE_DISPLAY}
            </a>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background/80">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-flex text-primary">
                <Star />
              </span>
              <strong className="text-background">4,6</strong> Google-Bewertung
            </span>
            <span className="hidden text-background/40 md:inline">•</span>
            <span>14 Rezensionen</span>
            <span className="hidden text-background/40 md:inline">•</span>
            <span>Fachbetrieb nach WHG</span>
            <span className="hidden text-background/40 md:inline">•</span>
            <span>Präqualifizierung (PQ)</span>
          </div>

          <div className="mt-5 hidden grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-x-3 sm:gap-y-2 md:grid">
            {[
              "20+ Jahre Erfahrung",
              "Familiengeführter Fachbetrieb",
              "Fachbetrieb nach WHG",
              "Präqualifizierung (PQ)",
            ].map((b) => (
              <span
                key={b}
                className="inline-flex items-center gap-1.5 rounded-md border border-background/30 bg-background/10 px-2.5 py-1.5 text-[11px] font-medium text-background backdrop-blur"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {b}
              </span>
            ))}
          </div>
          </div>

          {/* Hero contact form */}
          <div className="lg:col-span-5">
            <HeroLeadForm
              pageId="industrieboden_v1"
              formName="Industrieboden V1 Hero Formular"
              variant="industrie"
              phoneTel={PHONE_TEL}
              phoneDisplay={PHONE_DISPLAY}
              topicOptions={[
                "Industriebodenbeschichtung",
                "Bodenbeschichtung im Bestand",
                "WHG-Bereich",
                "ESD / ableitfähig",
                "Werkstatt / Lager / Produktion",
                "Sonstiges gewerbliches Projekt",
              ]}
            />
            <p className="mt-4 text-center text-xs leading-relaxed text-background/75 lg:text-left">
              Nicht sicher, ob Ihr Boden für eine neue Beschichtung geeignet
              ist? Genau dafür ist die erste Einschätzung da.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Pain points (B2B operational issues) ─── */
function PainPoints() {
  const items = [
    {
      problem: "Rissige, staubende oder stark abgenutzte Böden",
      outcome:
        "Belastbare Flächen, die im täglichen Werkstatt- und Hallenbetrieb wieder verlässlich nutzbar sind.",
    },
    {
      problem: "Hohe Belastung in Werkstatt, Halle und Produktion",
      outcome:
        "Systeme, die zu Stapler-, Maschinen- und Mitarbeiterverkehr passen – statt Standardlösungen von der Stange.",
    },
    {
      problem: "Schwer zu reinigende oder rutschige Flächen",
      outcome:
        "Leichter zu reinigende, sicherere Oberflächen mit passender Rutschhemmung für den jeweiligen Bereich.",
    },
    {
      problem: "Zustand des Bodens im laufenden Betrieb",
      outcome:
        "Realistische Planung und abgestimmte Umsetzung, die Stillstand und Ausfallzeiten möglichst gering hält.",
    },
    {
      problem: "Anforderungen in WHG-, ESD- oder Sonderbereichen",
      outcome:
        "Fachgerechte Systemwahl als Fachbetrieb nach WHG – mit Erfahrung in regulierten und technisch sensiblen Bereichen.",
    },
    {
      problem: "Unsicherheit bei der wirtschaftlich sinnvollen Lösung",
      outcome:
        "Ehrliche Einschätzung zu Aufbau, Lebensdauer und Kosten – statt Maximalangebot ohne Bezug zur Nutzung.",
    },
  ];
  return (
    <Section id="probleme" gray>
      <div className="mb-10 max-w-3xl">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Typische Probleme im Betrieb
        </div>
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Typische Probleme bei Industrieböden in Werkstatt, Halle und Produktion
        </h2>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          Industrieböden sind kein Detail – sie entscheiden mit über Sicherheit,
          Reinigung, Ausfallzeiten und Wirtschaftlichkeit. Diese Themen
          begegnen uns in gewerblichen und industriellen Projekten besonders
          häufig.
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <div
            key={it.problem}
            className="flex h-full flex-col rounded-xl border border-border bg-background p-6 shadow-sm"
          >
            <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">
              Problem
            </div>
            <h3 className="mt-1.5 text-base font-semibold tracking-tight text-foreground">
              {it.problem}
            </h3>
            <div className="mt-4 border-t border-border pt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Unsere Antwort
              </div>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {it.outcome}
              </p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <button
          type="button"
          onClick={scrollToForm}
          className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Projekt unverbindlich anfragen
        </button>
        <a
          href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v1", PHONE_TEL)}
          className="inline-flex h-11 items-center justify-center rounded-md border border-border bg-background px-6 text-sm font-semibold text-foreground transition-colors hover:border-foreground"
        >
          {PHONE_DISPLAY}
        </a>
      </div>
    </Section>
  );
}

/* ─── Trust strip ─── */
function TrustStrip() {
  const items = [
    { label: "4,6 ★ Google-Bewertung", strong: true },
    { label: "14 Google-Rezensionen" },
    { label: "Fachbetrieb nach WHG" },
    { label: "20+ Jahre Erfahrung" },
    { label: "Für Gewerbe und Industrie" },
  ];
  return (
    <div className="border-b border-border bg-muted/40">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-4 py-5 text-sm text-muted-foreground md:justify-between">
        {items.map((it) => (
          <span
            key={it.label}
            className={`inline-flex items-center gap-2 ${it.strong ? "font-semibold text-foreground" : ""}`}
          >
            {it.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── Reviews ─── */
function Reviews() {
  const reviews = [
    {
      text: "Herr Schneider berät ehrlich und nicht nur, um zu verkaufen. Die Beratung war passend zum Objekt und mir wurde eine andere Lösung empfohlen.",
      author: "Hannah Luisa Vimpolsek",
    },
    {
      text: "Einwandfreie und termingerechte Ausführung.",
      author: "Florian Heil",
    },
    {
      text: "Der Service ist einwandfrei und professionell.",
      author: "Sergio B.",
    },
  ];
  return (
    <Section gray>
      <div className="mb-10 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Vertrauen, das sich in echten Bewertungen zeigt
        </h2>
        <p className="mt-3 text-base text-muted-foreground">
          Rückmeldungen aus realen Projekten bestätigen vor allem Beratung,
          Professionalität und termingerechte Ausführung.
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-3">
        {reviews.map((r) => (
          <figure
            key={r.author}
            className="flex flex-col rounded-xl border border-border bg-background p-6 shadow-sm"
          >
            <div className="mb-3 flex items-center gap-1 text-primary">
              <Star />
              <Star />
              <Star />
              <Star />
              <Star />
            </div>
            <blockquote className="flex-1 text-sm leading-relaxed text-foreground">
              „{r.text}"
            </blockquote>
            <figcaption className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{r.author}</span>
              <span className="block">Google-Rezension</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </Section>
  );
}

/* ─── Featured project proof ─── */
function ProjectProof() {
  const cards = [
    {
      img: finishImg,
      title: "Großflächige Industriehalle mit hochwertiger Beschichtung",
      text: "Saubere, gleichmäßige und belastbare Flächenlösung für gewerblich genutzte Hallen mit hohem Anspruch an Optik, Pflege und Funktion.",
      featured: true,
    },
    {
      img: ableitImg,
      title: "Technische Flächen mit ableitfähiger Beschichtung",
      text: "Bei funktionalen Anforderungen wie ableitfähigen Systemen kommt es auf die fachgerechte Abstimmung von Untergrund, Nutzung und Beschichtungsaufbau an.",
    },
    {
      img: anwendungImg,
      title: "Fachgerechte Ausführung im laufenden Projekt",
      text: "Belastbare Ergebnisse entstehen durch saubere Vorbereitung, gewissenhafte Verarbeitung und eine kontrollierte Umsetzung auf der Fläche.",
    },
    {
      img: detailImg,
      title: "Detailansicht aus der Verarbeitung",
      text: "Echte Projekteinblicke zeigen die handwerkliche Qualität und den fachgerechten Umgang mit anspruchsvollen Bestandsflächen.",
    },
  ];
  return (
    <Section id="referenzen">
      <div className="mb-10 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Ausgewählte Einblicke aus dem gewerblichen und industriellen Umfeld
        </h2>
        <p className="mt-3 text-base text-muted-foreground">
          Reale Projektbilder zeigen, worauf es in der Praxis ankommt: saubere
          Ausführung, passende Systeme und belastbare Ergebnisse für gewerblich
          genutzte Flächen.
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        {cards.map((c, i) => (
          <article
            key={c.title}
            className={`overflow-hidden rounded-xl border border-border bg-background shadow-sm ${c.featured ? "md:col-span-2" : ""}`}
          >
            <div className="aspect-[16/10] overflow-hidden bg-muted">
              <img
                src={c.img}
                alt={c.title}
                loading={i === 0 ? "eager" : "lazy"}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="p-6">
              <h3 className="text-lg font-semibold tracking-tight text-foreground">
                {c.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {c.text}
              </p>
            </div>
          </article>
        ))}
      </div>
      <div className="mt-10 flex justify-center">
        <button
          type="button"
          onClick={scrollToForm}
          className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Projekt anfragen
        </button>
      </div>
    </Section>
  );
}

/* ─── Use cases / Leistungen ─── */
function UseCases() {
  const cases = [
    "Werkstätten",
    "Lagerhallen",
    "Produktionsflächen",
    "Technikräume",
    "Bestandsflächen",
    "WHG / ableitfähige Systeme",
  ];
  return (
    <Section id="leistungen" gray>
      <div className="grid gap-10 md:grid-cols-12 md:items-center md:gap-12">
        <div className="order-2 md:order-1 md:col-span-6">
          <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Passende Lösungen für stark beanspruchte gewerbliche Flächen
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            In Werkstätten, Lagerhallen, Produktionsbereichen, Technikräumen und
            anderen gewerblich genutzten Flächen kommt es nicht auf irgendeine
            Beschichtung an, sondern auf eine Lösung, die zum Untergrund, zur
            Nutzung und zur tatsächlichen Belastung passt.
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {cases.map((c) => (
              <li
                key={c}
                className="flex items-center gap-2.5 rounded-md border border-border bg-background px-4 py-3 text-sm font-medium text-foreground"
              >
                <CheckIcon />
                {c}
              </li>
            ))}
          </ul>
        </div>
        <div className="order-1 md:order-2 md:col-span-6">
          <div className="overflow-hidden rounded-xl border border-border bg-muted shadow-sm">
            <img
              src={useCaseImg}
              alt="Werkstatt mit professioneller Bodenbeschichtung in Gewerbeumgebung"
              loading="lazy"
              className="aspect-[4/3] h-full w-full object-cover"
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ─── Why Schneider ─── */
function WhySchneider() {
  const cards = [
    {
      title: "Ehrliche Beratung statt Standardverkauf",
      text: "Nicht jede Fläche braucht das gleiche System. Entscheidend ist, was zum Objekt, zur Nutzung und zum Zustand des Untergrunds passt.",
    },
    {
      title: "Passende Systemwahl für den Einsatzbereich",
      text: "Werkstatt, Lagerhalle, Produktion oder technischer Sonderbereich – die Beschichtung muss zur tatsächlichen Beanspruchung passen.",
    },
    {
      title: "Saubere Ausführung im gewerblichen Alltag",
      text: "Terminverbindlichkeit, gründliche Vorbereitung und eine fachgerechte Umsetzung sind bei gewerblich genutzten Flächen entscheidend.",
    },
    {
      title: "Erfahrung mit anspruchsvollen Anforderungen",
      text: "Auch Beschichtungen im Bestand, WHG-orientierte Anwendungen und ableitfähige Systeme erfordern eine saubere technische Abstimmung.",
    },
  ];
  const proofs = [
    "Fachbetrieb nach WHG",
    "Präqualifizierung (PQ)",
    "Gewerbe & Industrie",
    "Projektorientierte Lösungen",
  ];
  return (
    <Section id="warum-schneider">
      <div className="mb-10 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Warum Schneider Bodenbeschichtungen
        </h2>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        {cards.map((c, i) => (
          <div
            key={c.title}
            className="rounded-xl border border-border bg-background p-6 shadow-sm"
          >
            <div className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-sm font-semibold text-primary">
              {String(i + 1).padStart(2, "0")}
            </div>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">
              {c.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {c.text}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 border-t border-border pt-6 text-xs text-muted-foreground">
        {proofs.map((p) => (
          <span key={p} className="inline-flex items-center gap-1.5">
            <span className="h-1 w-1 rounded-full bg-primary" />
            {p}
          </span>
        ))}
      </div>
    </Section>
  );
}

/* ─── Erfahrung & Regionalität (Authority) ─── */
function Authority() {
  return (
    <Section id="erfahrung-regionalitaet" gray>
      <div className="grid gap-10 md:grid-cols-12 md:items-center md:gap-14">
        <div className="md:col-span-5">
          <div className="overflow-hidden rounded-xl border border-border bg-muted shadow-sm">
            <img
              src={teamImg}
              alt="Maurice Schneider und Andreas Schneider, Geschäftsführung Schneider Bodenbeschichtungen GmbH"
              loading="lazy"
              width={1024}
              height={1024}
              className="aspect-[4/5] h-full w-full object-cover"
            />
          </div>
          <div className="mt-3 text-xs font-medium text-muted-foreground">
            Maurice Schneider &amp; Andreas Schneider
          </div>
        </div>
        <div className="md:col-span-7">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Erfahrung · Kompetenz · Region
          </div>
          <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Erfahrung, Kompetenz und Regionalität
          </h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>
              Die Schneider Bodenbeschichtungen GmbH ist ein familiengeführter
              Fachbetrieb mit über 20 Jahren Erfahrung im Bereich der
              Bodenbeschichtungen. Unser Firmensitz befindet sich in Moers am
              Niederrhein – von hier aus betreuen wir Projekte in ganz NRW und
              auf Wunsch auch darüber hinaus.
            </p>
            <p>
              Unser geschultes und erfahrenes Team ist ein kompetenter Partner
              und verlässlicher Berater für unterschiedlichste Anforderungen:
              Industrieböden, Tiefgaragen, WHG-Beschichtungen und
              ESD-konforme Systeme. Mit moderner Technik und praxisnaher
              Erfahrung realisieren wir Projekte wirtschaftlich, zuverlässig
              und nach hohen Qualitätsstandards.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { v: "20+", l: "Jahre Erfahrung" },
              { v: "2", l: "Standorte in NRW" },
              { v: "WHG", l: "Fachbetrieb" },
              { v: "PQ", l: "Präqualifiziert" },
            ].map((s) => (
              <div
                key={s.l}
                className="rounded-lg border border-border bg-background p-4"
              >
                <div className="text-xl font-semibold text-foreground">
                  {s.v}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {s.l}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-lg border border-border bg-background p-6">
            <h3 className="text-base font-semibold tracking-tight text-foreground">
              Regional stark aufgestellt in Nordrhein-Westfalen
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Mit Standorten in Moers und Wuppertal ist Schneider
              Bodenbeschichtungen regional stark aufgestellt und betreut
              Projekte in ganz Nordrhein-Westfalen – vom Niederrhein über das
              Ruhrgebiet und das Bergische Land bis in den Großraum Köln.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
                <div className="font-semibold text-foreground">Moers</div>
                <div className="mt-0.5 text-muted-foreground">
                  Eurotec-Ring 36 · 47445 Moers
                </div>
              </div>
              <div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
                <div className="font-semibold text-foreground">Wuppertal</div>
                <div className="mt-0.5 text-muted-foreground">
                  Kreuzstraße 20 · 42277 Wuppertal
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ─── Technical expertise + WHG/ESD ─── */
function Technical() {
  const blocks = [
    {
      title: "Bestandsflächen technisch sinnvoll neu beschichten",
      text: "Viele Projekte starten nicht auf einem Neubau, sondern auf vorhandenen, bereits beanspruchten Flächen. Nach Prüfung von Untergrund und Zustand lässt sich ein passendes Beschichtungssystem abstimmen.",
    },
    {
      title: "WHG- und Sonderanforderungen berücksichtigen",
      text: "Je nach Projekt können WHG-Bezug, belastete Untergründe oder andere technische Rahmenbedingungen eine besondere Systemwahl erforderlich machen.",
    },
    {
      title: "Ableitfähige und funktionale Systeme einplanen",
      text: "In bestimmten Einsatzbereichen sind ableitfähige oder andere funktionale Eigenschaften relevant. Ausschlaggebend ist immer die tatsächliche Nutzung der Fläche.",
    },
  ];
  return (
    <Section id="whg-esd">
      <div className="mb-10 max-w-3xl">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          WHG · ESD · Beschichtung im Bestand
        </div>
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Industrieböden fachgerecht beschichten und Bestandsflächen weiter nutzbar machen
        </h2>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {blocks.map((b) => (
          <div
            key={b.title}
            className="rounded-xl border border-border bg-background p-6 shadow-sm"
          >
            <h3 className="text-base font-semibold tracking-tight text-foreground">
              {b.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {b.text}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-xl border border-border bg-muted/40 p-6 md:p-8">
        <h3 className="text-xl font-semibold tracking-tight text-foreground md:text-2xl">
          Fachbetrieb nach WHG &amp; präqualifizierter Partner
        </h3>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Wir sind als Fachbetrieb nach WHG zertifiziert und verfügen über die
          erforderliche Präqualifizierung für Bauvorhaben mit besonderen
          Anforderungen. Vertrauen Sie auf geprüfte Kompetenz und rechtssichere
          Umsetzung.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {[
            "Fachbetrieb nach WHG",
            "Präqualifizierung (PQ)",
            "ESD-konforme Systeme",
            "Beschichtung Bestand",
          ].map((b) => (
            <span
              key={b}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {b}
            </span>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ─── Process ─── */
function Process() {
  const steps = [
    {
      n: "01",
      title: "Anfrage senden",
      text: "Sie übermitteln die wichtigsten Eckdaten zu Fläche, Nutzung und Projektziel.",
    },
    {
      n: "02",
      title: "Anforderungen prüfen",
      text: "Untergrund, Beanspruchung und technische Rahmenbedingungen werden eingeordnet.",
    },
    {
      n: "03",
      title: "Passende Lösung abstimmen",
      text: "Auf dieser Basis wird eine technisch und wirtschaftlich sinnvolle Vorgehensweise abgestimmt.",
    },
    {
      n: "04",
      title: "Angebot und Umsetzung",
      text: "Danach kann das Projekt klar geplant und fachgerecht umgesetzt werden.",
    },
  ];
  return (
    <Section gray>
      <div className="mb-10 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          So läuft ein Projekt ab
        </h2>
      </div>
      <ol className="grid gap-5 md:grid-cols-4">
        {steps.map((s) => (
          <li
            key={s.n}
            className="rounded-xl border border-border bg-background p-6 shadow-sm"
          >
            <div className="text-sm font-semibold tracking-wider text-primary">
              {s.n}
            </div>
            <h3 className="mt-2 text-base font-semibold tracking-tight text-foreground">
              {s.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {s.text}
            </p>
          </li>
        ))}
      </ol>
    </Section>
  );
}

/* ─── FAQ ─── */
function FAQ() {
  const items = [
    {
      q: "Für welche Flächen eignet sich eine Industriebodenbeschichtung?",
      a: "Geeignet sind unter anderem Werkstätten, Lagerhallen, Produktionsflächen, Technikräume und andere gewerblich stark beanspruchte Bereiche.",
    },
    {
      q: "Lassen sich bestehende Industrieböden neu beschichten?",
      a: "Ja. Häufig geht es darum, vorhandene Flächen technisch zu prüfen und mit einem passenden Beschichtungssystem für die weitere Nutzung vorzubereiten.",
    },
    {
      q: "Spielen Epoxidharz-Systeme bei Industrieböden eine Rolle?",
      a: "Je nach Einsatzbereich können Epoxidharz-Systeme sinnvoll sein. Entscheidend ist jedoch immer die passende Lösung für Untergrund, Nutzung und Projektziel.",
    },
    {
      q: "Sind auch WHG- oder ableitfähige Systeme möglich?",
      a: "Ja. In bestimmten gewerblichen und industriellen Einsatzbereichen können WHG-orientierte oder ableitfähige Systeme erforderlich sein.",
    },
    {
      q: "In welchen Regionen sind Sie aktiv?",
      a: "Mit Standorten in Moers und Wuppertal betreuen wir Projekte in ganz Nordrhein-Westfalen – vom Niederrhein über das Ruhrgebiet und das Bergische Land bis in den Großraum Köln. Auf Wunsch auch darüber hinaus.",
    },
    {
      q: "Werden auch kleinere private Projekte angeboten?",
      a: "Nein. Der Fokus liegt auf gewerblichen und industriellen Projekten.",
    },
  ];
  return (
    <Section id="faq">
      <div className="mb-10 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Häufige Fragen zur Industriebodenbeschichtung
        </h2>
      </div>
      <Accordion type="single" collapsible className="mx-auto max-w-3xl">
        {items.map((it, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className="border-b border-border"
          >
            <AccordionTrigger className="py-5 text-left text-base font-semibold text-foreground hover:no-underline">
              {it.q}
            </AccordionTrigger>
            <AccordionContent className="pb-5 text-sm leading-relaxed text-muted-foreground">
              {it.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Section>
  );
}

/* ─── Final CTA ─── */
function FinalCTA() {
  return (
    <Section id="projektanfrage" gray>
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-foreground px-8 py-12 text-center text-background md:px-12 md:py-16">
        <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
          Projektanfrage für gewerblich genutzte Flächen
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-background/75">
          Wer eine belastbare Beschichtung oder eine technisch sinnvolle
          Beschichtung für eine gewerblich oder industriell genutzte Fläche plant,
          kann die wichtigsten Projektdaten direkt übermitteln.
        </p>
        <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={scrollToForm}
            className="inline-flex h-12 items-center justify-center rounded-md bg-primary px-7 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Projekt anfragen
          </button>
          <a
            href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v1", PHONE_TEL)}
            className="inline-flex h-12 items-center justify-center rounded-md border border-background/30 px-7 text-sm font-semibold text-background transition-colors hover:bg-background/10"
          >
            Jetzt anrufen
          </a>
        </div>
      </div>
    </Section>
  );
}

/* ─── Contact form ─── */
function ContactForm() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();
    setStatus("sending");
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);
    const payload = Object.fromEntries(fd.entries()) as Record<string, string>;
    try {
      const { submitLead } = await import("@/lib/submit-lead");
      await submitLead(
        {
          name: payload.name || "",
          firma: payload.firma || "",
          email: payload.email || "",
          telefon: payload.telefon || "",
          ort: payload.ort || "",
          betreff: payload.betreff || "",
          nachricht: payload.nachricht || "",
        },
        "industrieboden_v1",
        payload.website || "",
        formEl
      );
      if (typeof window !== "undefined") {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: "generate_lead",
          page_id: "industrieboden_v1",
        });
      }
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  const inputClass =
    "w-full rounded-md border border-border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground";
  const labelClass = "mb-1.5 block text-xs font-medium text-foreground";

  return (
    <Section id="kontakt">
      <div className="grid gap-10 md:grid-cols-12 md:gap-12">
        <div className="md:col-span-5">
          <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Erste Einschätzung für Ihre Industriebodenbeschichtung anfragen
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Senden Sie uns kurz die wichtigsten Eckdaten zu Objekt, Fläche und
            Zustand des Bodens. Wir prüfen Ihre Anfrage und melden uns in der
            Regel innerhalb von 24 Stunden mit einer ersten Einschätzung.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {[
              "Unverbindlich",
              "Für gewerbliche / industrielle Objekte",
              "Antwort in 24 Stunden",
            ].map((p) => (
              <span
                key={p}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {p}
              </span>
            ))}
          </div>
          <p className="mt-5 rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm leading-relaxed text-foreground">
            Nicht sicher, ob Ihr Boden für eine neue Beschichtung geeignet ist?
            Genau dafür ist die erste Einschätzung da.
          </p>
          <div className="mt-8 space-y-5 text-sm text-foreground">
            <div className="rounded-lg border border-border bg-muted/30 p-5">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Telefon
              </div>
              <a
                href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v1", PHONE_TEL)}
                className="mt-1.5 inline-flex items-center gap-2 text-xl font-semibold tracking-tight text-foreground hover:text-primary"
              >
                <svg viewBox="0 0 20 20" className="h-5 w-5 fill-current text-primary" aria-hidden="true">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                {PHONE_DISPLAY}
              </a>
              <p className="mt-2 text-xs text-muted-foreground">
                Direkter Kontakt für gewerbliche und industrielle Projekte.
              </p>
            </div>

            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                E-Mail
              </div>
              <a
                href={`mailto:${EMAIL}`}
                onClick={() => trackEmailClick("industrieboden_v1", EMAIL)}
                className="mt-1 inline-block text-sm text-foreground underline-offset-4 hover:text-primary hover:underline"
              >
                {EMAIL}
              </a>
            </div>

            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Anschrift
              </div>
              <div className="mt-1 text-sm text-foreground">
                Schneider Bodenbeschichtungen GmbH
                <br />
                Eurotec-Ring 36
                <br />
                47445 Moers
              </div>
            </div>

            <ul className="space-y-2 border-t border-border pt-5 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Ausschließlich für gewerbliche und industrielle Anfragen</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Persönliche Einschätzung für Ihr Projekt</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Fachbetrieb nach WHG · Präqualifizierung (PQ)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Familiengeführter Fachbetrieb mit über 20 Jahren Erfahrung</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="md:col-span-7">
          {status === "success" ? (
            <div
              role="status"
              aria-live="polite"
              className="rounded-2xl border-2 border-primary bg-background p-6 shadow-2xl shadow-primary/30 md:p-8"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12l5 5L20 7" />
                </svg>
              </div>
              <h3 className="mt-5 text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
                Vielen Dank für Ihre Anfrage
              </h3>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                Wir haben Ihre Anfrage erhalten. Wir prüfen Ihre Angaben und
                melden uns in der Regel innerhalb von 24 Stunden mit einer
                ersten Einschätzung.
              </p>
              <p className="mt-4 text-sm text-foreground">
                Sie erreichen uns auch direkt unter{" "}
                <a
                  href={`tel:${PHONE_TEL}`}
                  onClick={() => trackPhoneClick("industrieboden_v1", PHONE_TEL)}
                  className="font-semibold text-primary hover:underline"
                >
                  {PHONE_DISPLAY}
                </a>
                .
              </p>
            </div>
          ) : (
          <div className="relative">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-2 rounded-2xl bg-primary/10 blur-2xl"
            />
            <form
              onSubmit={onSubmit}
              id="industrieboden_v1_kontakt"
              data-gj-no-native-submit="1"
              data-form-id="industrieboden_v1_kontakt"
              data-form-name="Industrieboden V1 Abschluss-Formular"
              className="relative rounded-2xl border-2 border-primary bg-background p-6 shadow-2xl shadow-primary/30 md:p-8"
            >
              <AttributionFields />
              <div className="-mt-10 mb-5 flex justify-center md:justify-start">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary-foreground shadow-md">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                  Kostenlose Erstberatung
                </span>
              </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className={labelClass}>
                  Ihr Name *
                </label>
                <input id="name" name="name" required className={inputClass} />
              </div>
              <div>
                <label htmlFor="firma" className={labelClass}>
                  Firma *
                </label>
                <input id="firma" name="firma" required className={inputClass} />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>
                  Ihre E-Mail-Adresse *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="telefon" className={labelClass}>
                  Telefonnummer *
                </label>
                <input
                  id="telefon"
                  name="telefon"
                  type="tel"
                  required
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="ort" className={labelClass}>
                  Ort / Projektstandort
                </label>
                <input id="ort" name="ort" className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="betreff" className={labelClass}>
                  Worum geht es? *
                </label>
                <select
                  id="betreff"
                  name="betreff"
                  required
                  defaultValue=""
                  className={inputClass}
                >
                  <option value="" disabled>
                    Bitte wählen
                  </option>
                  <option>Industriebodenbeschichtung</option>
                  <option>Bodenbeschichtung im Bestand</option>
                  <option>WHG-Bereich</option>
                  <option>ESD / ableitfähig</option>
                  <option>Werkstatt / Lager / Produktion</option>
                  <option>Sonstiges gewerbliches Projekt</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="nachricht" className={labelClass}>
                  Kurze Beschreibung *
                </label>
                <textarea
                  id="nachricht"
                  name="nachricht"
                  required
                  rows={5}
                  placeholder="z. B. Produktionshalle, Lagerfläche oder Werkstattboden, ca. 500 m², Boden abgenutzt, staubt oder soll neu beschichtet werden."
                  className={inputClass}
                />
              </div>
            </div>

            {/* Honeypot */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

            <button
              type="submit"
              disabled={status === "sending"}
              className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:bg-primary/90 hover:shadow-lg disabled:opacity-60"
            >
              {status === "sending"
                ? "Wird gesendet…"
                : "Erste Einschätzung anfragen"}
            </button>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              Nach dem Absenden prüfen wir Ihre Angaben. Wenn das Projekt passt,
              melden wir uns zur ersten Einschätzung oder zur Abstimmung der
              nächsten Schritte.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Pflichtfelder mit *. Nur für gewerbliche und industrielle Anfragen.
            </p>

            {status === "error" && (
              <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                Beim Senden ist etwas schiefgegangen. Bitte versuchen Sie es
                erneut oder rufen Sie uns an.
              </p>
            )}
            </form>
          </div>
          )}
        </div>
      </div>
    </Section>
  );
}

/* ─── Footer ─── */
function Footer() {
  return (
    <footer className="bg-background pb-24 pt-10 md:pb-10">
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid gap-6 border-t border-border pt-8 md:grid-cols-12 md:gap-8">
          <div className="md:col-span-5 text-xs text-muted-foreground">
            <div className="font-semibold text-foreground">
              Schneider Bodenbeschichtungen GmbH
            </div>
            <div className="mt-1">Eurotec-Ring 36 · 47445 Moers</div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              <a href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("industrieboden_v1", PHONE_TEL)} className="hover:text-foreground">
                {PHONE_DISPLAY}
              </a>
              <a href={`mailto:${EMAIL}`} onClick={() => trackEmailClick("industrieboden_v1", EMAIL)} className="hover:text-foreground">
                {EMAIL}
              </a>
            </div>
          </div>
          <div className="md:col-span-4 text-xs text-muted-foreground">
            <div className="font-semibold text-foreground">Standort Wuppertal</div>
            <div className="mt-1">Kreuzstraße 20 · 42277 Wuppertal</div>
          </div>
          <nav className="md:col-span-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground/80 md:justify-end">
            <a
              href="https://schneider-bodenbeschichtungen.de/impressum/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground"
            >
              Impressum
            </a>
            <a
              href="https://schneider-bodenbeschichtungen.de/datenschutzerklaerung/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground"
            >
              Datenschutz
            </a>
            <a
              href="https://schneider-bodenbeschichtungen.de/agb/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground"
            >
              AGB
            </a>
          </nav>
        </div>
        <div className="mt-6 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Schneider Bodenbeschichtungen GmbH
        </div>
      </div>
    </footer>
  );
}

/* ─── Page ─── */
function LandingV2() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <StickyDesktopCTA />
      <main>
        <Hero />
        <TrustStrip />
        <PainPoints />
        <Reviews />
        <ProjectProof />
        <UseCases />
        <WhySchneider />
        <Authority />
        <Technical />
        <Process />
        <FAQ />
        <FinalCTA />
        <ContactForm />
      </main>
      <Footer />
      <StickyMobileCTA />
    </div>
  );
}
